import notification from 'antd/lib/notification'
import { useUserDetails } from 'Contexts/UserDetailsContext'
import { StudioConfigType } from 'Core/Studio'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ErrorCode } from 'Constants/constants'
import { getAssignmentAtStep, getEnabledPluginsAtStep } from 'Services/tasks.service'
import { ApiError } from 'Types/Common.types'
import { ContextType } from 'Types/Tasks.types'

type StudioConfigDataType = Omit<StudioConfigType, 'onNextTask' | 'onExitTask' | 'featureFlags'>

export const useFetchAssignment = (options?: { enabled?: boolean }) => {
    const enabled = options?.enabled !== false
    const [studioConfigData, setStudioConfigData] = useState<StudioConfigDataType>()
    const { stepId } = useParams()
    const userDetails = useUserDetails()
    const location = useLocation()
    const navigate = useNavigate()
    const [id, setId] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const fetchAssignmentAndInit = useCallback(async () => {
        if (!stepId || !enabled) return
        try {
            setIsLoading(true)
            const assignmentData = await getAssignmentAtStep(
                userDetails?.userID ?? '',
                stepId,
                location.state?.projectId
            )
            if (!assignmentData) {
                throw new Error('Failed to fetch assignment')
            }
            const pluginsConfig = await getEnabledPluginsAtStep(
                assignmentData.assignmentResp.assignment.task.id || ''
            )
            const config: StudioConfigDataType = {
                taskType: assignmentData?.assignmentResp?.assignment.task.type,
                interfaceKey: assignmentData?.assignmentResp.assignment.interface_key,
                taskConfig: {
                    assignment: assignmentData?.assignmentResp?.assignment,
                    submissionId: assignmentData?.submissionResp?.task_submission.id,
                    mode: assignmentData.assignmentResp.assignment.step?.type,
                    useCase: assignmentData.assignmentResp.assignment.task.type,
                    jsonSchemaMetaData:
                        assignmentData.assignmentResp.assignment.task.json_schema_metadata
                            ?.metadata,
                    jobTags: assignmentData?.assignmentResp?.assignment.job_tags,
                    userTags: assignmentData?.assignmentResp?.assignment.user_tags,
                },
                context: {
                    type: ContextType.NormalTask,
                },
                pluginsConfig,
            }
            setId(assignmentData?.assignmentResp?.assignment.id || '')
            setStudioConfigData(config)
        } catch (error) {
            const errorObj = error as ApiError
            const errorMessage = errorObj.message
            const errorCode = errorObj.code

            if (errorCode === ErrorCode.DAILY_LIMIT_EXCEEDED) {
                notification.warning({
                    message: 'Daily tasks limit exceeded',
                    description: errorMessage,
                    placement: 'bottomLeft',
                })
                navigate(`/`, { replace: true })
                return
            }
            if (errorMessage === 'No task available on the selected StepId ') {
                notification.info({
                    message: 'No more tasks are available in this task queue. Please check later',
                    placement: 'bottomLeft',
                })
            } else {
                notification.error({
                    message: (error as Error).message ?? 'Error in opening task',
                    description:
                        'Please try again. If you still face the issue, reach out to the project owner',
                    placement: 'bottomLeft',
                })
            }
            navigate(`/`, { replace: true })
        } finally {
            setIsLoading(false)
        }
    }, [enabled, location.state?.projectId, navigate, stepId, userDetails?.userID])

    useEffect(() => {
        if (!enabled) {
            setIsLoading(false)
            return
        }
        if (!studioConfigData) {
            fetchAssignmentAndInit().then(() => {
                window.onbeforeunload = function (e) {
                    e.preventDefault()
                    return 'Are you sure you want to leave? Your progress will be lost.'
                }
            })
        }
    }, [enabled, fetchAssignmentAndInit, studioConfigData])

    return {
        id,
        studioConfigData,
        isLoading: enabled ? isLoading : false,
        refetch: fetchAssignmentAndInit,
    }
}
